import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, ImagePlus, Lock, Check, Crown, Sparkles, Wand2, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { IconRenderer } from "../lib/icons";
import { fileToDataUrl } from "../lib/image";
import { speak } from "../lib/speech";
import Header from "../components/Header";
import Modal from "../components/Modal";
import IconPicker from "../components/IconPicker";

const CATEGORIES = [
  { id: "needs", label: "Needs", cls: "cat-needs" },
  { id: "feelings", label: "Feelings", cls: "cat-feelings" },
  { id: "food", label: "Food", cls: "cat-food" },
  { id: "activities", label: "Activities", cls: "cat-activities" },
  { id: "health", label: "Health", cls: "cat-health" },
  { id: "people", label: "People", cls: "cat-people" },
];

const emptyTile = { label: "", icon: "Star", category: "needs", image: null };

export default function BoardEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUsePro } = useAuth();
  const [board, setBoard] = useState(null);
  const [filter, setFilter] = useState("needs");
  const [modal, setModal] = useState(null); // null | { tile, tileId? }
  const [suggestModal, setSuggestModal] = useState(null); // null | { tiles, added:Set }
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [aiImgBusy, setAiImgBusy] = useState(false);
  const [library, setLibrary] = useState(null); // null | { tiles } (open when set)
  const [libraryCat, setLibraryCat] = useState("needs");
  const [libraryBusy, setLibraryBusy] = useState(false);
  const libraryData = useRef(null);
  const fileInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/boards/${id}`);
      setBoard(data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/app");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveTile = async () => {
    const t = modal.tile;
    if (!t.label.trim()) return toast.error("Give the tile a label");
    try {
      if (modal.tileId) await api.patch(`/boards/${id}/tiles/${modal.tileId}`, t);
      else await api.post(`/boards/${id}/tiles`, t);
      setModal(null);
      await load();
      toast.success("Tile saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteTile = async (tileId) => {
    try {
      await api.delete(`/boards/${id}/tiles/${tileId}`);
      await load();
      toast.success("Tile removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file, 256);
      setModal((m) => ({ ...m, tile: { ...m.tile, image: dataUrl } }));
    } catch {
      toast.error("Could not read that photo");
    }
  };

  const generateTileImage = async () => {
    const label = modal?.tile?.label?.trim();
    if (!label) return toast.error("Give the tile a label first");
    setAiImgBusy(true);
    try {
      const { data } = await api.post("/ai/image", { label });
      setModal((m) => ({ ...m, tile: { ...m.tile, image: data.image } }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAiImgBusy(false);
    }
  };

  const suggestTiles = async () => {
    setSuggestBusy(true);
    try {
      const { data } = await api.post("/ai/tiles", { existing_labels: board.tiles.map((t) => t.label) });
      if (data.tiles.length === 0) toast.info("No new suggestions right now — try again later");
      else setSuggestModal({ tiles: data.tiles, added: new Set() });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSuggestBusy(false);
    }
  };

  const addSuggestedTile = async (tile) => {
    try {
      await api.post(`/boards/${id}/tiles`, tile);
      setSuggestModal((m) => ({ ...m, added: new Set([...m.added, tile.label]) }));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openLibrary = async () => {
    if (!libraryData.current) {
      setLibraryBusy(true);
      try {
        const { data } = await api.get("/symbol-library");
        libraryData.current = data.tiles;
      } catch (e) {
        toast.error(apiError(e));
        setLibraryBusy(false);
        return;
      }
      setLibraryBusy(false);
    }
    setLibraryCat(filter);
    setLibrary({ added: new Set() });
  };

  const addLibraryTile = async (tile) => {
    try {
      await api.post(`/boards/${id}/tiles`, tile);
      setLibrary((m) => ({ ...m, added: new Set([...m.added, tile.label]) }));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const printBoard = () => {
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const byCat = CATEGORIES.map((c) => ({ ...c, tiles: board.tiles.filter((t) => t.category === c.id) })).filter((c) => c.tiles.length);
    const w = window.open("", "_blank");
    if (!w) return toast.error("Pop-up blocked — allow pop-ups to print");
    w.document.write(`<html><head><title>${esc(board.name)}</title><style>body{font-family:sans-serif;padding:32px;color:#0A192F}h1{font-size:32px}h2{font-size:22px;margin:24px 0 8px}.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}.tile{border:3px solid #0A192F;border-radius:16px;padding:18px;text-align:center;font-size:20px;font-weight:800}</style></head><body><h1>${esc(board.name)}</h1>${byCat.map((c) => `<h2>${c.label}</h2><div class="grid">${c.tiles.map((t) => `<div class="tile">${esc(t.label)}</div>`).join("")}</div>`).join("")}<p style="margin-top:32px;color:#666">Made with RoutineBuddy — free at ${window.location.origin}</p></body></html>`);
    w.document.close();
    w.print();
  };

  if (!board) return <div className="min-h-screen bg-background"><Header /><p className="p-8 text-lg">Loading…</p></div>;

  const tiles = board.tiles.filter((t) => t.category === filter);
  const atLimit = !canUsePro && board.tiles.length >= 20;

  return (
    <div data-testid="board-editor" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
            <ArrowLeft size={20} strokeWidth={2.5} /> Back
          </button>
          {!canUsePro && (
            <span data-testid="tile-limit-indicator" className="text-sm font-semibold text-muted-foreground">
              Free board: {board.tiles.length} of 20 tiles
            </span>
          )}
        </div>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight">{board.name}</h1>
        <p className="mt-1 text-lg text-muted-foreground">Tap a tile to hear it. Edit or remove tiles below.</p>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            data-testid="browse-library-button"
            onClick={openLibrary}
            disabled={libraryBusy}
            className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60"
          >
            <Plus size={22} strokeWidth={2.5} /> {libraryBusy ? "Loading…" : "Browse symbol library"}
          </button>
          <button
            data-testid="print-board-button"
            onClick={printBoard}
            className="min-h-[56px] px-6 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200"
          >
            <Printer size={22} strokeWidth={2.5} /> Print board
          </button>
          {canUsePro ? (
            <button
              data-testid="ai-suggest-tiles-button"
              onClick={suggestTiles}
              disabled={suggestBusy}
              className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60"
            >
              <Sparkles size={22} strokeWidth={2.5} /> {suggestBusy ? "Thinking…" : "Suggest tiles with AI"}
            </button>
          ) : (
            <button
              data-testid="ai-suggest-locked-button"
              onClick={() => navigate("/premium")}
              className="min-h-[56px] px-6 rounded-full border-2 border-dashed border-foreground/25 bg-secondary/50 font-bold flex items-center gap-2 text-muted-foreground"
            >
              <Lock size={22} strokeWidth={2.5} /> Suggest tiles with AI (Pro)
            </button>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`editor-category-tab-${c.id}`}
              onClick={() => setFilter(c.id)}
              className={`min-h-[52px] px-5 rounded-full border-2 font-bold text-base transition-colors duration-200 ${
                filter === c.id ? "border-primary bg-primary/10" : "border-foreground/20 bg-white hover:bg-secondary"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div data-testid="editor-tile-grid" className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {tiles.map((t) => {
            const cat = CATEGORIES.find((c) => c.id === t.category) || CATEGORIES[0];
            return (
              <div key={t.id} data-testid={`editor-tile-${t.id}`} className={`relative bg-white rounded-3xl border-2 ${cat.cls} p-4 flex flex-col items-center gap-2`}>
                <button
                  onClick={() => speak(t.label)}
                  aria-label={`Hear ${t.label}`}
                  className="w-20 h-20 rounded-2xl grid place-items-center overflow-hidden hover:bg-secondary transition-colors duration-200"
                >
                  {t.image ? <img src={t.image} alt="" className="w-full h-full object-cover rounded-2xl" /> : <IconRenderer name={t.icon} size={44} />}
                </button>
                <p className="text-lg font-extrabold text-center leading-tight">{t.label}</p>
                <div className="flex gap-2">
                  <button data-testid={`edit-tile-${t.id}`} onClick={() => setModal({ tile: { label: t.label, icon: t.icon, category: t.category, image: t.image }, tileId: t.id })} aria-label={`Edit ${t.label}`} className="min-w-[44px] min-h-[44px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200"><Pencil size={18} strokeWidth={2.5} /></button>
                  <button data-testid={`delete-tile-${t.id}`} onClick={() => deleteTile(t.id)} aria-label={`Delete ${t.label}`} className="min-w-[44px] min-h-[44px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200"><Trash2 size={18} strokeWidth={2.5} /></button>
                </div>
              </div>
            );
          })}
          <button
            data-testid="add-tile-button"
            onClick={() => (atLimit ? navigate("/premium") : setModal({ tile: { ...emptyTile, category: filter } }))}
            className="min-h-[180px] rounded-3xl border-2 border-dashed border-foreground/30 bg-white font-bold text-lg flex flex-col items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200"
          >
            {atLimit ? <Crown size={32} strokeWidth={2.5} /> : <Plus size={32} strokeWidth={3} />}
            {atLimit ? "Upgrade for more tiles" : "Add tile"}
          </button>
        </div>
      </main>

      {library && (
        <Modal title="Symbol library" onClose={() => setLibrary(null)} testId="library-modal">
          <div className="flex flex-wrap gap-2 mb-4">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`library-tab-${c.id}`}
                onClick={() => setLibraryCat(c.id)}
                className={`min-h-[44px] px-4 rounded-full border-2 font-bold text-sm transition-colors duration-200 ${
                  libraryCat === c.id ? "border-primary bg-primary/10" : "border-foreground/20 bg-white hover:bg-secondary"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 max-h-[50vh] overflow-y-auto">
            {(libraryData.current || []).filter((t) => t.category === libraryCat).map((t) => {
              const added = library.added.has(t.label) || board.tiles.some((x) => x.label.toLowerCase() === t.label.toLowerCase());
              return (
                <button
                  key={t.label}
                  data-testid={`library-tile-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => !added && !atLimit && addLibraryTile(t)}
                  disabled={added || atLimit}
                  className={`rounded-2xl border-2 p-3 flex flex-col items-center gap-1 transition-colors duration-200 ${
                    added ? "border-[#48BB78]/50 bg-[#48BB78]/10 opacity-60" : "border-foreground/15 bg-white hover:bg-secondary"
                  }`}
                >
                  {added ? <Check size={32} strokeWidth={3} className="text-[#2F855A]" /> : <IconRenderer name={t.icon} size={32} />}
                  <span className="text-sm font-extrabold text-center leading-tight">{t.label}</span>
                </button>
              );
            })}
          </div>
          {atLimit && <p className="mt-3 text-sm font-bold text-[#B7791F]">Free boards hold 20 tiles — upgrade for unlimited.</p>}
        </Modal>
      )}

      {suggestModal && (
        <Modal title="AI tile suggestions" onClose={() => setSuggestModal(null)} testId="ai-suggest-modal">
          <p className="text-base text-muted-foreground font-semibold mb-4">Tap a suggestion to add it to your board.</p>
          <div className="space-y-3">
            {suggestModal.tiles.map((t) => {
              const added = suggestModal.added.has(t.label) || board.tiles.some((x) => x.label.toLowerCase() === t.label.toLowerCase());
              const cat = CATEGORIES.find((c) => c.id === t.category) || CATEGORIES[0];
              return (
                <button
                  key={t.label}
                  data-testid={`ai-suggested-tile-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => !added && addSuggestedTile(t)}
                  disabled={added}
                  className={`w-full min-h-[64px] rounded-2xl border-2 ${cat.cls} bg-white px-4 flex items-center gap-3 text-left transition-colors duration-200 ${added ? "opacity-50" : "hover:bg-secondary"}`}
                >
                  <IconRenderer name={t.icon} size={32} />
                  <span className="flex-1 text-lg font-extrabold">{t.label}</span>
                  <span className="text-sm font-bold text-muted-foreground capitalize">{t.category}</span>
                  {added ? <Check size={24} strokeWidth={3} className="text-[#2F855A]" /> : <Plus size={24} strokeWidth={3} className="text-primary" />}
                </button>
              );
            })}
          </div>
        </Modal>
      )}

      {modal && (
        <Modal title={modal.tileId ? "Edit tile" : "Add a tile"} onClose={() => setModal(null)} testId="tile-modal">
          <label htmlFor="tile-label" className="block text-base font-bold mb-1">Label (spoken aloud)</label>
          <input
            id="tile-label"
            data-testid="tile-label-input"
            value={modal.tile.label}
            onChange={(e) => setModal((m) => ({ ...m, tile: { ...m.tile, label: e.target.value } }))}
            placeholder="e.g. I want juice"
            className="w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
          />
          <p className="mt-4 mb-2 text-base font-bold">Category</p>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`tile-category-${c.id}`}
                onClick={() => setModal((m) => ({ ...m, tile: { ...m.tile, category: c.id } }))}
                className={`min-h-[48px] px-4 rounded-full border-2 font-bold text-sm transition-colors duration-200 ${
                  modal.tile.category === c.id ? "border-primary bg-primary/10" : "border-foreground/20 bg-white hover:bg-secondary"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <p className="mt-4 mb-2 text-base font-bold">Icon</p>
          <IconPicker value={modal.tile.icon} onChange={(name) => setModal((m) => ({ ...m, tile: { ...m.tile, icon: name, image: null } }))} />
          <p className="mt-4 mb-2 text-base font-bold">Photo {!canUsePro && <span className="text-[#B7791F]">(Pro)</span>}</p>
          {canUsePro ? (
            <div className="flex flex-wrap items-center gap-3">
              <input type="file" accept="image/*" className="hidden" ref={fileInput} onChange={(e) => uploadPhoto(e.target.files[0])} />
              <button data-testid="tile-photo-upload-button" onClick={() => fileInput.current?.click()} className="min-h-[56px] px-5 rounded-2xl border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
                <ImagePlus size={22} strokeWidth={2.5} /> Upload photo
              </button>
              <button data-testid="tile-ai-image-button" onClick={generateTileImage} disabled={aiImgBusy} className="min-h-[56px] px-5 rounded-2xl bg-[#9F7AEA] text-white font-bold flex items-center gap-2 transition-colors duration-200 hover:opacity-90 disabled:opacity-60">
                <Wand2 size={22} strokeWidth={2.5} /> {aiImgBusy ? "Drawing…" : "Draw with AI"}
              </button>
              {modal.tile.image && <img src={modal.tile.image} alt="Tile" className="w-14 h-14 rounded-2xl object-cover border-2 border-foreground/15" />}
            </div>
          ) : (
            <button data-testid="tile-photo-locked-button" onClick={() => navigate("/premium")} className="min-h-[56px] px-5 rounded-2xl border-2 border-dashed border-foreground/25 bg-secondary/50 font-bold flex items-center gap-2 text-muted-foreground">
              <Lock size={22} strokeWidth={2.5} /> Unlock photo tiles with Premium
            </button>
          )}
          <button
            data-testid="save-tile-button"
            onClick={saveTile}
            className="mt-6 w-full min-h-[56px] rounded-full bg-primary text-white text-lg font-bold flex items-center justify-center gap-2"
          >
            <Check size={24} strokeWidth={3} /> Save tile
          </button>
        </Modal>
      )}
    </div>
  );
}
